import { apiClient } from '@/shared/api/client';

export interface RegisterApiParams {
    keyTypeName?: string;
    entityCd?: string;
}

export interface RegisterApiItem {
    id: string;
    keyTypeName: string | null;
    countryCode: string | null;
    registerWork: string | null;
    jsonData: Record<string, any> | null;
    rawJson: string | Record<string, any> | null;
    rgstCd?: string;
    entityCd?: string;
    sysRegName?: string | null;
    registerType?: string | null;
}

interface RegisterApiResponse {
    registers: RegisterApiItem[];
    total: number;
    keyTypeName: string | null;
}

export interface EntityOption {
    value: string;
    title: string;
}

interface EntityListResponse {
    entities: EntityOption[];
}

export const fetchRegisterEntries = async (params: RegisterApiParams = {}): Promise<RegisterApiItem[]> => {
    // 디버깅: API 호출 전 로그
    console.log('🌐 [registerApi] fetchRegisterEntries 호출:', {
        params,
        url: '/register',
        timestamp: new Date().toISOString()
    });

    const response = await apiClient.get<RegisterApiResponse>('/register', {
        params
    });

    // 디버깅: API 응답 로그
    console.log('🌐 [registerApi] fetchRegisterEntries 응답:', {
        success: response.success,
        data: response.data ? {
            total: response.data.total,
            keyTypeName: response.data.keyTypeName,
            registersCount: response.data.registers?.length || 0,
            sampleRegisters: response.data.registers?.slice(0, 3).map(r => ({
                id: r.id,
                rgstCd: r.rgstCd,
                entityCd: r.entityCd,
                keyTypeName: r.keyTypeName
            })) || []
        } : null,
        error: response.error,
        timestamp: new Date().toISOString()
    });

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch registers');
    }

    const registers = response.data?.registers ?? [];
    
    // 디버깅: 반환 데이터 로그
    console.log('🌐 [registerApi] fetchRegisterEntries 반환:', {
        count: registers.length,
        allKeyTypeNames: [...new Set(registers.map(r => r.keyTypeName).filter(Boolean))]
    });

    return registers;
};

export const fetchEntityList = async (): Promise<EntityOption[]> => {
    const response = await apiClient.get<EntityListResponse>('/register/entities');

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entities');
    }

    return response.data?.entities ?? [];
};

/**
 * Register 문서 생성 (DOCX 또는 PDF)
 * @param keyTypeName - KEY_TYPE_NAME (예: 'Register of Directors')
 * @param entityCd - ENTITY_CD
 * @param format - 'docx' | 'pdf' (기본값: 'docx')
 * @returns Promise<Blob> - 생성된 문서 파일
 */
export const generateRegisterDocument = async (
    keyTypeName: string,
    entityCd: string,
    format: 'docx' | 'pdf' = 'docx',
    sources?: string[],
    entityCdL?: string | null,
    entityCdMig?: string | null,
): Promise<Blob> => {
    // apiClient의 인스턴스를 사용하여 blob 응답 처리
    const axiosInstance = apiClient.getInstance();
    
    try {
        const response = await axiosInstance.post(
            `/register/document/generate?format=${format}`,
            { keyTypeName, entityCd, sources, entityCdL, entityCdMig },
            { 
                responseType: 'blob',
                validateStatus: (status) => status < 500 // 500 이상만 에러로 처리
            }
        );

        // 응답이 에러인지 확인 (Content-Type이 application/json이면 에러)
        // Axios 헤더 값 타입이 string | string[] 등이라 includes 전에 문자열로 좁힘
        const rawCt = response.headers['content-type'];
        const contentType: string =
            typeof rawCt === 'string'
                ? rawCt
                : Array.isArray(rawCt)
                  ? String(rawCt[0] ?? '')
                  : '';
        if (contentType.includes('application/json') || response.status >= 400) {
            const errorText = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read error response'));
                reader.readAsText(response.data);
            });
            
            try {
                const errorData = JSON.parse(errorText);
                // 백엔드 에러 응답 구조에 맞게 메시지 추출
                const errorMessage = errorData?.error?.message || 
                                    errorData?.error?.detail || 
                                    errorData?.message || 
                                    errorData?.detail || 
                                    'Failed to generate document';
                throw new Error(errorMessage);
            } catch (parseError) {
                throw new Error(errorText || 'Failed to generate document');
            }
        }

        return response.data as Blob;
    } catch (error: any) {
        // 에러 응답이 Blob인 경우 JSON으로 파싱
        if (error.response?.data instanceof Blob) {
            try {
                const errorText = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = () => reject(new Error('Failed to read error response'));
                    reader.readAsText(error.response.data);
                });
                
                try {
                    const errorData = JSON.parse(errorText);
                    // 백엔드 에러 응답 구조에 맞게 메시지 추출
                    const errorMessage = errorData?.error?.message || 
                                        errorData?.error?.detail || 
                                        errorData?.message || 
                                        errorData?.detail || 
                                        'Failed to generate document';
                    throw new Error(errorMessage);
                } catch (parseError) {
                    throw new Error(errorText || 'Failed to generate document');
                }
            } catch (readError) {
                // Blob 읽기 실패 시 원본 에러 메시지 사용
                throw new Error(error?.message || 'Failed to generate document');
            }
        }
        
        // 이미 Error 객체인 경우 그대로 throw
        if (error instanceof Error) {
            throw error;
        }
        
        throw new Error(error?.message || 'Failed to generate document');
    }
};

export const generateRegisterDocumentsBatch = async (
    keyTypeNames: string[],
    entityCd: string,
    format: 'docx' | 'pdf' = 'docx',
    sources?: string[],
    entityCdL?: string | null,
    entityCdMig?: string | null,
    excludeCessated?: boolean,
): Promise<Blob> => {
    const axiosInstance = apiClient.getInstance();

    try {
        const response = await axiosInstance.post(
            `/register/document/generate-batch?format=${format}`,
            { keyTypeNames, entityCd, sources, entityCdL, entityCdMig, excludeCessated: !!excludeCessated },
            {
                responseType: 'blob',
                validateStatus: (status) => status < 500
            }
        );

        const rawCt = response.headers['content-type'];
        const contentType: string =
            typeof rawCt === 'string'
                ? rawCt
                : Array.isArray(rawCt)
                  ? String(rawCt[0] ?? '')
                  : '';
        if (contentType.includes('application/json') || response.status >= 400) {
            const errorText = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read error response'));
                reader.readAsText(response.data);
            });

            try {
                const errorData = JSON.parse(errorText);
                const errorMessage = errorData?.error?.message ||
                                    errorData?.error?.detail ||
                                    errorData?.message ||
                                    errorData?.detail ||
                                    'Failed to generate merged document';
                throw new Error(errorMessage);
            } catch (parseError) {
                throw new Error(errorText || 'Failed to generate merged document');
            }
        }

        return response.data as Blob;
    } catch (error: any) {
        if (error.response?.data instanceof Blob) {
            try {
                const errorText = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = () => reject(new Error('Failed to read error response'));
                    reader.readAsText(error.response.data);
                });

                try {
                    const errorData = JSON.parse(errorText);
                    const errorMessage = errorData?.error?.message ||
                                        errorData?.error?.detail ||
                                        errorData?.message ||
                                        errorData?.detail ||
                                        'Failed to generate merged document';
                    throw new Error(errorMessage);
                } catch (parseError) {
                    throw new Error(errorText || 'Failed to generate merged document');
                }
            } catch (readError) {
                throw new Error(error?.message || 'Failed to generate merged document');
            }
        }

        if (error instanceof Error) {
            throw error;
        }

        throw new Error(error?.message || 'Failed to generate merged document');
    }
};

export interface RegisterDetail {
    rgstCd: string;
    entityCd: string;
    companyName?: string;
    clientCd: string;
    registerWork: string | null;
    sysregdate: string | null;
    keyTypeName?: string | null;
    jsonData: Record<string, any> | null;
    rawJson: string | Record<string, any> | null;
    sharesList?: Array<Record<string, any>>;
}

interface RegisterDetailResponse {
    register: RegisterDetail;
}

export const fetchRegisterDetail = async (
    rgstCd: string,
    entityCd: string,
    opts?: { preferredMasterWork?: string | null }
): Promise<RegisterDetail> => {
    const params: Record<string, string> = { entityCd };
    if (opts?.preferredMasterWork) {
        params.preferredMasterWork = String(opts.preferredMasterWork).trim();
    }
    const response = await apiClient.get<{ data: RegisterDetail }>(`/register/${rgstCd}`, {
        params
    });

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch register detail');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterDirectorParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterDirector = async (params: UpdateRegisterDirectorParams): Promise<UpdateRegisterDirectorResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterDirectorResponse }>(
        `/register/director/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register director');
    }

    return response.data?.data ?? response.data as any;
};

export const updateRegisterOwners = async (params: UpdateRegisterDirectorParams): Promise<UpdateRegisterDirectorResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterDirectorResponse }>(
        `/register/owners/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register owners');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterDirectorParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterDirector = async (params: CreateRegisterDirectorParams): Promise<CreateRegisterDirectorResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterDirectorResponse }>(
        '/register/director',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register director');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterManagingDirectorParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterManagingDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterManagingDirector = async (params: UpdateRegisterManagingDirectorParams): Promise<UpdateRegisterManagingDirectorResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterManagingDirectorResponse }>(
        `/register/managing-director/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register managing director');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterManagingDirectorParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterManagingDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterManagingDirector = async (params: CreateRegisterManagingDirectorParams): Promise<CreateRegisterManagingDirectorResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterManagingDirectorResponse }>(
        '/register/managing-director',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register managing director');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterOwnersParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterOwnersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterOwners = async (params: CreateRegisterOwnersParams): Promise<CreateRegisterOwnersResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterOwnersResponse }>(
        '/register/owners',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register owners');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterRepresentativesParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterRepresentativesResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterRepresentatives = async (params: UpdateRegisterRepresentativesParams): Promise<UpdateRegisterRepresentativesResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterRepresentativesResponse }>(
        `/register/representatives/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register representatives');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterRepresentativesParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterRepresentativesResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterRepresentatives = async (params: CreateRegisterRepresentativesParams): Promise<CreateRegisterRepresentativesResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterRepresentativesResponse }>(
        '/register/representatives',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register representatives');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterDPOParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterDPOResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterDPO = async (params: CreateRegisterDPOParams): Promise<CreateRegisterDPOResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterDPOResponse }>(
        '/register/dpo',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register DPO');
    }

    return response.data?.data ?? response.data as any;
};

export const updateRegisterDPO = async (params: UpdateRegisterDirectorParams): Promise<UpdateRegisterDirectorResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterDirectorResponse }>(
        `/register/dpo/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register DPO');
    }

    return response.data?.data ?? response.data as any;
};

export interface DeleteRegisterParams {
    rgstCd: string;
    entityCd: string;
}

export interface DeleteRegisterResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const deleteRegister = async (params: DeleteRegisterParams): Promise<DeleteRegisterResponse> => {
    const response = await apiClient.delete<{ data: DeleteRegisterResponse }>(
        `/register/${params.rgstCd}`,
        {
            params: {
                entityCd: params.entityCd
            }
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete register');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterSecretariesParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterSecretariesResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterSecretaries = async (params: UpdateRegisterSecretariesParams): Promise<UpdateRegisterSecretariesResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterSecretariesResponse }>(
        `/register/secretaries/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register secretaries');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterSecretariesParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterSecretariesResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterSecretaries = async (params: CreateRegisterSecretariesParams): Promise<CreateRegisterSecretariesResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterSecretariesResponse }>(
        '/register/secretaries',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register secretaries');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterAuditorsParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterAuditorsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterAuditors = async (params: UpdateRegisterAuditorsParams): Promise<UpdateRegisterAuditorsResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterAuditorsResponse }>(
        `/register/auditors/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register auditors');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterAuditorsParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterAuditorsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterAuditors = async (params: CreateRegisterAuditorsParams): Promise<CreateRegisterAuditorsResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterAuditorsResponse }>(
        '/register/auditors',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register auditors');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterNomineeDirectorsParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterNomineeDirectorsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterNomineeDirectors = async (params: UpdateRegisterNomineeDirectorsParams): Promise<UpdateRegisterNomineeDirectorsResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterNomineeDirectorsResponse }>(
        `/register/nominee-directors/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update nominee directors');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterNomineeDirectorsParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterNomineeDirectorsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterNomineeDirectors = async (params: CreateRegisterNomineeDirectorsParams): Promise<CreateRegisterNomineeDirectorsResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterNomineeDirectorsResponse }>(
        '/register/nominee-directors',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create nominee directors');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterControllersParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterControllersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterControllers = async (params: UpdateRegisterControllersParams): Promise<UpdateRegisterControllersResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterControllersResponse }>(
        `/register/controllers/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register controllers');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterControllersParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterControllersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterControllers = async (params: CreateRegisterControllersParams): Promise<CreateRegisterControllersResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterControllersResponse }>(
        '/register/controllers',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register controllers');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterAllotmentsParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterAllotmentsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterAllotments = async (params: UpdateRegisterAllotmentsParams): Promise<UpdateRegisterAllotmentsResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterAllotmentsResponse }>(
        `/register/allotments/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register allotments');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterAllotmentsParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterAllotmentsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterAllotments = async (params: CreateRegisterAllotmentsParams): Promise<CreateRegisterAllotmentsResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterAllotmentsResponse }>(
        '/register/allotments',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register allotments');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterNomineeShareholdersParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterNomineeShareholdersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterNomineeShareholders = async (params: UpdateRegisterNomineeShareholdersParams): Promise<UpdateRegisterNomineeShareholdersResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterNomineeShareholdersResponse }>(
        `/register/nominee-shareholders/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update nominee shareholders');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterNomineeShareholdersParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterNomineeShareholdersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterNomineeShareholders = async (params: CreateRegisterNomineeShareholdersParams): Promise<CreateRegisterNomineeShareholdersResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterNomineeShareholdersResponse }>(
        '/register/nominee-shareholders',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create nominee shareholders');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterAddressParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterAddressResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterAddress = async (params: UpdateRegisterAddressParams): Promise<UpdateRegisterAddressResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterAddressResponse }>(
        `/register/address/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register address');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterAddressParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterAddressResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterAddress = async (params: CreateRegisterAddressParams): Promise<CreateRegisterAddressResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterAddressResponse }>(
        '/register/address',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register address');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterDirectorShareholdingsParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterDirectorShareholdingsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterDirectorShareholdings = async (params: UpdateRegisterDirectorShareholdingsParams): Promise<UpdateRegisterDirectorShareholdingsResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterDirectorShareholdingsResponse }>(
        `/register/director-shareholdings/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register director shareholdings');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterDirectorShareholdingsShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface UpdateRegisterDirectorShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterDirectorShareholdingsShare = async (params: UpdateRegisterDirectorShareholdingsShareParams): Promise<UpdateRegisterDirectorShareholdingsShareResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterDirectorShareholdingsShareResponse }>(
        `/register/director-shareholdings/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterDirectorShareholdingsShareParams {
    rgstCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface CreateRegisterDirectorShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterDirectorShareholdingsShare = async (params: CreateRegisterDirectorShareholdingsShareParams): Promise<CreateRegisterDirectorShareholdingsShareResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterDirectorShareholdingsShareResponse }>(
        `/register/director-shareholdings/${params.rgstCd}/shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create share');
    }

    return response.data?.data ?? response.data as any;
};

export interface ValidateRegisterDirectorShareholdingsShareParams {
    entityCd: string;
    shareData: Record<string, any>;
}

export const validateRegisterDirectorShareholdingsShare = async (params: ValidateRegisterDirectorShareholdingsShareParams): Promise<void> => {
    const response = await apiClient.post<{ data: { message: string } }>(
        `/register/director-shareholdings/validate-shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Validation failed');
    }
};

export interface DeleteRegisterDirectorShareholdingsShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
}

export interface DeleteRegisterDirectorShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const deleteRegisterDirectorShareholdingsShare = async (params: DeleteRegisterDirectorShareholdingsShareParams): Promise<DeleteRegisterDirectorShareholdingsShareResponse> => {
    const response = await apiClient.delete<{ data: DeleteRegisterDirectorShareholdingsShareResponse }>(
        `/register/director-shareholdings/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            data: {
                entityCd: params.entityCd
            }
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterDirectorShareholdingsParams {
    entityCd: string;
    jsonData: Record<string, any>;
    sharesList?: Array<Record<string, any>>;
}

export interface CreateRegisterDirectorShareholdingsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterDirectorShareholdings = async (params: CreateRegisterDirectorShareholdingsParams): Promise<CreateRegisterDirectorShareholdingsResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterDirectorShareholdingsResponse }>(
        '/register/director-shareholdings',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData,
            sharesList: params.sharesList || []
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register director shareholdings');
    }

    return response.data?.data ?? response.data as any;
};

// CEO Shareholdings API
export interface UpdateRegisterCEOShareholdingsParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterCEOShareholdingsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterCEOShareholdings = async (params: UpdateRegisterCEOShareholdingsParams): Promise<UpdateRegisterCEOShareholdingsResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterCEOShareholdingsResponse }>(
        `/register/ceo-shareholdings/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register CEO shareholdings');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterCEOShareholdingsShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface UpdateRegisterCEOShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterCEOShareholdingsShare = async (params: UpdateRegisterCEOShareholdingsShareParams): Promise<UpdateRegisterCEOShareholdingsShareResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterCEOShareholdingsShareResponse }>(
        `/register/ceo-shareholdings/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterCEOShareholdingsShareParams {
    rgstCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface CreateRegisterCEOShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterCEOShareholdingsShare = async (params: CreateRegisterCEOShareholdingsShareParams): Promise<CreateRegisterCEOShareholdingsShareResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterCEOShareholdingsShareResponse }>(
        `/register/ceo-shareholdings/${params.rgstCd}/shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create share');
    }

    return response.data?.data ?? response.data as any;
};

export interface ValidateRegisterCEOShareholdingsShareParams {
    entityCd: string;
    shareData: Record<string, any>;
}

export const validateRegisterCEOShareholdingsShare = async (params: ValidateRegisterCEOShareholdingsShareParams): Promise<void> => {
    const response = await apiClient.post<{ data: { message: string } }>(
        `/register/ceo-shareholdings/validate-shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Validation failed');
    }
};

export interface DeleteRegisterCEOShareholdingsShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
}

export interface DeleteRegisterCEOShareholdingsShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const deleteRegisterCEOShareholdingsShare = async (params: DeleteRegisterCEOShareholdingsShareParams): Promise<DeleteRegisterCEOShareholdingsShareResponse> => {
    const response = await apiClient.delete<{ data: DeleteRegisterCEOShareholdingsShareResponse }>(
        `/register/ceo-shareholdings/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            data: {
                entityCd: params.entityCd
            }
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterCEOShareholdingsParams {
    entityCd: string;
    jsonData: Record<string, any>;
    sharesList?: Array<Record<string, any>>;
}

export interface CreateRegisterCEOShareholdingsResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterCEOShareholdings = async (params: CreateRegisterCEOShareholdingsParams): Promise<CreateRegisterCEOShareholdingsResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterCEOShareholdingsResponse }>(
        '/register/ceo-shareholdings',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData,
            sharesList: params.sharesList || []
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register CEO shareholdings');
    }

    return response.data?.data ?? response.data as any;
};

// Members API
export interface UpdateRegisterMembersParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterMembersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterMembers = async (params: UpdateRegisterMembersParams): Promise<UpdateRegisterMembersResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterMembersResponse }>(
        `/register/members/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register members');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterMembersShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface UpdateRegisterMembersShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterMembersShare = async (params: UpdateRegisterMembersShareParams): Promise<UpdateRegisterMembersShareResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterMembersShareResponse }>(
        `/register/members/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterMembersShareParams {
    rgstCd: string;
    entityCd: string;
    shareData: Record<string, any>;
}

export interface CreateRegisterMembersShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterMembersShare = async (params: CreateRegisterMembersShareParams): Promise<CreateRegisterMembersShareResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterMembersShareResponse }>(
        `/register/members/${params.rgstCd}/shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create share');
    }

    return response.data?.data ?? response.data as any;
};

export interface ValidateRegisterMembersShareParams {
    entityCd: string;
    shareData: Record<string, any>;
}

export const validateRegisterMembersShare = async (params: ValidateRegisterMembersShareParams): Promise<void> => {
    const response = await apiClient.post<{ data: { message: string } }>(
        `/register/members/validate-shares`,
        {
            entityCd: params.entityCd,
            shareData: params.shareData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Validation failed');
    }
};

export interface DeleteRegisterMembersShareParams {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
}

export interface DeleteRegisterMembersShareResponse {
    rgstCd: string;
    rgstShareCd: string;
    entityCd: string;
    message: string;
}

export const deleteRegisterMembersShare = async (params: DeleteRegisterMembersShareParams): Promise<DeleteRegisterMembersShareResponse> => {
    const response = await apiClient.delete<{ data: DeleteRegisterMembersShareResponse }>(
        `/register/members/${params.rgstCd}/shares/${params.rgstShareCd}`,
        {
            data: {
                entityCd: params.entityCd
            }
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete share');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterMembersParams {
    entityCd: string;
    jsonData: Record<string, any>;
    sharesList?: Array<Record<string, any>>;
}

export interface CreateRegisterMembersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterMembers = async (params: CreateRegisterMembersParams): Promise<CreateRegisterMembersResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterMembersResponse }>(
        '/register/members',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData,
            sharesList: params.sharesList || []
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register members');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterTransfersParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterTransfersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterTransfers = async (params: UpdateRegisterTransfersParams): Promise<UpdateRegisterTransfersResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterTransfersResponse }>(
        `/register/transfers/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register transfers');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterTransfersParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterTransfersResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterTransfers = async (params: CreateRegisterTransfersParams): Promise<CreateRegisterTransfersResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterTransfersResponse }>(
        '/register/transfers',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register transfers');
    }

    return response.data?.data ?? response.data as any;
};

// CEO API
export interface UpdateRegisterCEOParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterCEOResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterCEO = async (params: UpdateRegisterCEOParams): Promise<UpdateRegisterCEOResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterCEOResponse }>(
        `/register/ceo/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register CEO');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterCEOParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterCEOResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterCEO = async (params: CreateRegisterCEOParams): Promise<CreateRegisterCEOResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterCEOResponse }>(
        '/register/ceo',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register CEO');
    }

    return response.data?.data ?? response.data as any;
};

export interface UpdateRegisterAlternateDirectorParams {
    rgstCd: string;
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface UpdateRegisterAlternateDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const updateRegisterAlternateDirector = async (params: UpdateRegisterAlternateDirectorParams): Promise<UpdateRegisterAlternateDirectorResponse> => {
    const response = await apiClient.put<{ data: UpdateRegisterAlternateDirectorResponse }>(
        `/register/alternate-director/${params.rgstCd}`,
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update register alternate director');
    }

    return response.data?.data ?? response.data as any;
};

export interface CreateRegisterAlternateDirectorParams {
    entityCd: string;
    jsonData: Record<string, any>;
}

export interface CreateRegisterAlternateDirectorResponse {
    rgstCd: string;
    entityCd: string;
    message: string;
}

export const createRegisterAlternateDirector = async (params: CreateRegisterAlternateDirectorParams): Promise<CreateRegisterAlternateDirectorResponse> => {
    const response = await apiClient.post<{ data: CreateRegisterAlternateDirectorResponse }>(
        '/register/alternate-director',
        {
            entityCd: params.entityCd,
            jsonData: params.jsonData
        }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create register alternate director');
    }

    return response.data?.data ?? response.data as any;
};
